import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import css from './styles/your-accounts-tab.scss';
import { Dialog } from 'shamrock-clover-ui/dist/clover/components/Dialog/Dialog';
import { Button as ClovBtn } from 'shamrock-clover-ui/dist/clover/components/Button/Button';
import { SmrDropdown } from 'shamrock-clover-ui/dist/clover/components/Dropdown/Dropdown';
import { Button } from '@mui/material';
import ClientAutoSuggestion from './client-autosuggestion';
import YourAccountsTable from './your-accounts-table';
import LocalAuth from '@shamrock-core/common/authentication/local-authentication';

import {
  fetchCadenceAccounts,
  getCadenceAccounts,
  getIMUser,
  setIMUser,
  setActiveAccountInfo,
} from '../../redux/actions/appActions';
import { useTranslation } from 'react-i18next';

import { sortAlphabetically } from '../../utils/utils';
import FirebaseAnalytics from '../core/analytics';
import invoiceManagerService from '../../services/invoice-manager.service';
import { Spinner } from 'shamrock-clover-ui/dist/clover/components/Spinner/Spinner';

const YourAccountsTab = (props) => {
  const { changeAccount, onChangesSaved, onChangesCancelled } = props;
  const cadenceAccounts = useSelector(getCadenceAccounts);
  const imUser = useSelector(getIMUser);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [addAccountsDialogOpen, setAddAccountsDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);

  const [isSavingChanges, setIsSavingChanges] = useState(false);

  const cadenceAccountsCopy = [...(cadenceAccounts ? cadenceAccounts : [])];
  const currentAccounts = cadenceAccountsCopy.sort((a, b) =>
    sortAlphabetically(a.client_name?.toLowerCase(), b.client_name?.toLowerCase()),
  );

  const accountOptions = currentAccounts.map((account) => {
    return {
      firstColumn: account.client_number,
      secondColumn: account.client_name,
      thirdColumn: account.mc_number,
    };
  });
  const [addedAccounts, setAddedAccounts] = useState([]);
  const [accountsToRemove, setAccountsToRemove] = useState([]);
  const activeAccount = accountOptions.find((account) => account.firstColumn === imUser.active_account);
  const addToAddedAccounts = (accountToAdd) => {
    const index = addedAccounts.findIndex((acct) => accountToAdd.client_number === acct.client_number);
    if (index === -1) {
      FirebaseAnalytics.sendEvent(
        FirebaseAnalytics.EVENTS.CLICK,
        FirebaseAnalytics.MODULES.FACTORING,
        FirebaseAnalytics.PAGES.ADD_REMOVE_ACCOUNTS,
        {
          type: 'add',
        },
      );
      setAddedAccounts([accountToAdd, ...addedAccounts]);
    }
  };

  const resetDialog = () => {
    setAddedAccounts([]);
    setAccountsToRemove([]);
    setAddAccountsDialogOpen(false);
  };

  const handleCancelClick = () => {
    resetDialog();
    onChangesCancelled();
  };

  const accountsToDisable = useMemo(
    () => [...cadenceAccounts, ...addedAccounts],
    [JSON.stringify(addedAccounts), JSON.stringify(cadenceAccounts)],
  );

  const isRemovingActiveAccount = useMemo(
    () => accountsToRemove.map((acc) => acc.client_number).includes(imUser.active_account),
    [accountsToRemove, imUser],
  );

  const saveAccountChanges = async () => {
    try {
      setIsSavingChanges(true);
      const clientsToRemove = accountsToRemove.map((acc) => acc.client_number);
      const filteredAccounts = currentAccounts.filter((acc) => !clientsToRemove.includes(acc.client_number));
      const updatedAccountList = [...filteredAccounts, ...addedAccounts];
      const alphabeticalAccounts = updatedAccountList.sort((a, b) =>
        sortAlphabetically(a.client_name?.toLowerCase(), b.client_name?.toLowerCase()),
      );
      const formattedList = alphabeticalAccounts.map((acc) => ({
        reference_id: acc.client_number,
      }));

      const updatedUser = {
        user_id: LocalAuth.getUser().email as string,
        accounts: formattedList,
        active_account:
          isRemovingActiveAccount || !imUser.accounts.length ? formattedList[0]?.reference_id : imUser.active_account,
      };
      await invoiceManagerService.saveIMUser(imUser.user_id, updatedUser, true);

      const newUser = {
        ...imUser,
        ...updatedUser,
      };

      dispatch(setIMUser(newUser));
      dispatch(
        fetchCadenceAccounts(
          formattedList.map((acc) => acc.reference_id),
          newUser,
        ),
      );
      if (newUser?.accounts?.length === 0) {
        dispatch(setActiveAccountInfo({}));
      }
      setIsSavingChanges(false);
      onChangesSaved();
      resetDialog();
    } catch (error) {
      setIsSavingChanges(false);
      setErrorDialogOpen(true);
    }
  };

  const stagedCount = addedAccounts.length + currentAccounts.length - accountsToRemove.length;

  return (
    <div className={css.actionSection}>
      <div className={css.accountsDropdown}>
        <SmrDropdown
          hasTitle={true}
          onSelect={(option) => {
            changeAccount(option.firstColumn);
          }}
          defaultText="Type Client # or Name"
          config={{
            numberOfColumns: 3,
            firstColumnWidth: '120px',
            secondColumnWidth: '380px',
            thirdColumnWidth: '60px',
          }}
          maxOptionRender={10}
          disabledText="No accounts added"
          titleItem={{
            firstColumn: 'CLIENT #',
            secondColumn: 'CLIENT NAME',
            thirdColumn: 'MC #',
          }}
          options={accountOptions.length > 0 ? accountOptions : null}
          currentSelectedOption={activeAccount}
        />
      </div>
      <div className={css.buttonSection}>
        <Button style={{ textTransform: 'none', color: '#666666' }} onClick={() => setAddAccountsDialogOpen(true)}>
          Add/Remove Accounts
        </Button>
        {addAccountsDialogOpen && (
          <>
            <Dialog
              open={addAccountsDialogOpen}
              title="Add/Remove Accounts"
              width={'745'}
              content={
                <div className={css.viewAsAccountDialogOuterDiv}>
                  <span className={css.viewAsAccountDescription}>
                    Search by Client #, Client Name, or MC # of the new account you would like to add to your account.
                  </span>
                  <div className={css.viewAsAccountSubHeading}>{t('add_accounts')}</div>
                  <ClientAutoSuggestion
                    onSuggestionError={stagedCount >= 50 ? 'Remove accounts to add more to your account.' : null}
                    disabledSuggestions={accountsToDisable}
                    unsetValueOnSelect={true}
                    onOptionSelect={(selection) => {
                      addToAddedAccounts(selection);
                    }}
                  />
                  <YourAccountsTable
                    addedAccounts={addedAccounts}
                    accountsToRemove={accountsToRemove}
                    currentAccounts={currentAccounts}
                    setAddedAccounts={setAddedAccounts}
                    setAccountsToRemove={setAccountsToRemove}
                  />
                </div>
              }
              actions={
                <div className={css.dialogActionsContainer}>
                  <ClovBtn buttonStyle="outlined" onClick={handleCancelClick}>
                    Cancel
                  </ClovBtn>
                  <ClovBtn
                    style={{ width: 141 }}
                    onClick={saveAccountChanges}
                    disabled={addedAccounts.length === 0 && accountsToRemove.length === 0}
                  >
                    {isSavingChanges ? <Spinner size="25" color="white" /> : 'Save Changes'}
                  </ClovBtn>
                </div>
              }
            />
            {errorDialogOpen && (
              <Dialog
                open={errorDialogOpen}
                title="Unable to save changes."
                content={'Please try again.'}
                actions={
                  <div className={css.errorDialogActionsContainer}>
                    <ClovBtn onClick={() => setErrorDialogOpen(false)}>Dismiss</ClovBtn>
                  </div>
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default YourAccountsTab;
